<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import { useInfiniteScroll } from '@vueuse/core'
import type { Activity } from '@/types/customer/Activity'
import dayjs from 'dayjs'

import type { Icons } from '@/components/base/BaseIcon.vue'
import type { HistoryReconstructionStatus } from '@/types/customer/ActivitiesFeed'

const { t } = useI18n()

const props = defineProps<{
  activities: Activity[]
  height: number
  isLoaded: boolean
  reconstructionStatus?: HistoryReconstructionStatus | null
  hasMore?: boolean
  isLoadingMore?: boolean
}>()

const emit = defineEmits<{
  (e: 'loadMore'): void
  (e: 'filterChange', type: string | null): void
}>()

const scrollContainerRef = ref<HTMLElement | null>(null)

useInfiniteScroll(
  scrollContainerRef,
  () => {
    if (props.hasMore && !props.isLoadingMore) {
      emit('loadMore')
    }
  },
  {
    distance: 100,
    canLoadMore: () => props.hasMore === true && props.isLoadingMore === false
  }
)

const subtitle = t('pages.customer.customerDetailPage.blocks.activity.description')
const displaySubtitleOnMobile = true

const filtersType: { type: string | null; name: string; icon: Icons | null; color: string }[] = [
  {
    type: null,
    name: t('pages.customer.customerDetailPage.filters.all'),
    icon: null,
    color: 'text-yellow-400 group-hover:text-yellow-500'
  },
  {
    type: 'checkin',
    name: t('pages.customer.customerDetailPage.filters.checkin'),
    icon: 'HeroMapPinIconSolid',
    color: 'text-yellow-400 group-hover:text-yellow-500'
  },
  {
    type: 'answerCrm',
    name: t('pages.customer.customerDetailPage.filters.answerCrm'),
    icon: 'HeroIdentificationIconSolid',
    color: ' text-muted-foreground group-hover:text-muted-foreground'
  },
  {
    type: 'reward',
    name: t('pages.customer.customerDetailPage.filters.reward'),
    icon: 'HeroGiftIconSolid',
    color: 'text-primary/50 group-hover:text-primary'
  },
  {
    type: 'survey',
    name: t('pages.customer.customerDetailPage.filters.survey'),
    icon: 'HeroChartPieIconSolid',
    color: 'text-indigo-400 group-hover:text-indigo-500'
  },
  {
    type: 'message',
    name: t('pages.customer.customerDetailPage.filters.message'),
    icon: 'HeroChatBubbleOvalLeftEllipsisIconSolid',
    color: 'text-green-400 group-hover:text-green-500'
  },
  {
    type: 'contestGameParticipation',
    name: t('pages.customer.customerDetailPage.filters.contestGameParticipation'),
    icon: 'HeroPlayIconSolid',
    color: 'text-red-400 group-hover:text-red-500'
  },
  {
    type: 'manualPoint',
    name: t('pages.customer.customerDetailPage.filters.manualPoint'),
    icon: 'HeroCursorArrowRaysIconSolid',
    color: 'text-pink-600 group-hover:text-pink-900'
  },
  {
    type: 'missedCalls',
    name: t('pages.customer.customerDetailPage.filters.missedCalls'),
    icon: 'HeroPhoneXMarkIconSolid',
    color: 'text-indigo-400 group-hover:text-indigo-500'
  },
  {
    type: 'autopilot',
    name: t('pages.customer.customerDetailPage.filters.autopilot'),
    icon: 'HeroBoltIconSolid',
    color: 'text-yellow-400 group-hover:text-yellow-500'
  },
  {
    type: 'webapp',
    name: t('pages.customer.customerDetailPage.filters.webapp'),
    icon: 'HeroCircleStackIconSolid',
    color: ' text-amber-500 group-hover:text-amber-600'
  },
  {
    type: 'segment',
    name: t('pages.customer.customerDetailPage.filters.segment'),
    icon: 'HeroSquare3Stack3DIconSolid',
    color: 'text-blue-500 group-hover:text-blue-600'
  }
]

const activitiesType = ref<string | null>(null)

const filteredActivities = computed(() => {
  let filtered = [...props.activities]

  if (activitiesType.value) {
    filtered = filtered.filter((activity) => activity.getType() === activitiesType.value)
  }

  return filtered.sort((current, next) => {
    const currentDateTime = dayjs(`${current.getDate()} ${current.getTime()}`, 'DD/MM/YYYY HH:mm')
    const nextDateTime = dayjs(`${next.getDate()} ${next.getTime()}`, 'DD/MM/YYYY HH:mm')
    return nextDateTime.valueOf() - currentDateTime.valueOf()
  })
})

const handleFilterChange = (filterType: string | null) => {
  activitiesType.value = filterType
  emit('filterChange', filterType)
}
</script>

<template>
  <section
    aria-labelledby="timeline-title"
    class="mb-36 w-full overflow-hidden rounded-lg border bg-white sm:mb-0 lg:w-5/12"
  >
    <div class="h-full flex-1">
      <div class="border-b">
        <div class="mx-4 flex items-center justify-between py-5 sm:mx-6">
          <div class="flex-1">
            <div class="flex items-center space-x-0 sm:space-x-6">
              <div>
                <p class="text-base font-semibold text-foreground inline-flex items-center gap-2">
                  {{ t('pages.customer.customerDetailPage.blocks.activity.title') }}
                </p>
                <p
                  v-if="subtitle"
                  class="text-sm font-normal text-muted-foreground"
                  :class="{ 'hidden sm:inline-block': !displaySubtitleOnMobile }"
                >
                  {{ t('pages.customer.customerDetailPage.blocks.activity.description') }}
                </p>
              </div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="outline" class="w-full">
                <span v-if="activitiesType == null">{{ t('pages.customer.customerDetailPage.filters.all') }}</span>
                <span v-else>{{ t(`pages.customer.customerDetailPage.filters.${activitiesType}`) }}</span>
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-56">
              <DropdownMenuItem
                v-for="filterType in filtersType"
                :key="filterType.type"
                class="group flex cursor-pointer items-center px-4 py-2 text-sm text-foreground focus:bg-accent focus:text-foreground"
                @click="handleFilterChange(filterType.type)"
              >
                <BaseIcon
                  v-if="filterType.icon"
                  :name="filterType.icon"
                  class="mr-3 h-5 w-5"
                  :class="filterType.color"
                />
                {{ filterType.name }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div
        ref="scrollContainerRef"
        class="h-full overflow-y-auto px-4 pt-5 sm:px-6"
        :style="`max-height: ${height - 110}px;`"
      >
        <div v-if="!isLoaded" class="flex items-center justify-center py-4">
          <div class="flex items-center justify-center p-4">
            <div class="h-8 w-8 animate-spin rounded-full border-2 border-solid border-b-primary"></div>
          </div>
        </div>
        <div v-else-if="activities.length > 0 && filteredActivities?.length > 0" class="pr-2">
          <ul class="">
            <li v-for="(activity, index) in filteredActivities" :key="activity.getId()">
              <div class="relative pb-8">
                <span
                  v-if="index < filteredActivities?.length - 1"
                  class="absolute left-4 top-4 -ml-px h-full w-0.5 bg-stone-200"
                  aria-hidden="true"
                />
                <div class="relative flex space-x-3 items-center">
                  <div>
                    <span
                      class="flex h-8 w-8 items-center justify-center rounded-full ring-8 ring-white"
                      :class="activity.getIconColor()"
                    >
                      <BaseIcon v-if="activity.getIcon()" :name="activity.getIcon()" class="h-5 w-5 text-white" />
                    </span>
                  </div>
                  <div class="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                    <div>
                      <p class="flex text-sm text-muted-foreground">
                        <Popover v-if="activity.isDetaillable()">
                          <PopoverTrigger class="border-b border-dashed text-left focus:outline-hidden">
                            {{
                              activity.isFeedActivity()
                                ? activity.getText()
                                : t(activity.getText(), activity.getTextParams())
                            }}
                          </PopoverTrigger>
                          <PopoverContent class="w-80 overflow-hidden border p-0 shadow-lg">
                            <div class="bg-muted p-4">
                              <span class="flex items-center">
                                <span class="text-sm font-medium text-foreground">
                                  {{
                                    activity.isFeedActivity() ? activity.getDetailTitle() : t(activity.getDetailTitle())
                                  }}
                                </span>
                              </span>
                              <span v-if="activity.getDetailText()" class="mt-1 block text-sm text-muted-foreground">
                                {{ activity.getDetailText() }}
                              </span>
                            </div>
                            <span v-if="activity.isConverted()" class="block px-4 py-2 text-sm text-muted-foreground">
                              {{ t(activity.getConversionDateText(), activity.getConversionDateTextParams()) }}
                            </span>
                          </PopoverContent>
                        </Popover>
                        <span v-else>
                          {{
                            activity.isFeedActivity()
                              ? activity.getText()
                              : t(activity.getText(), activity.getTextParams())
                          }}
                        </span>
                      </p>
                    </div>
                    <div class="whitespace-nowrap text-right text-sm text-muted-foreground">
                      <div v-if="activity.getType() === 'message' && activity.isPending()">En attente</div>
                      <div v-else>
                        <div>{{ activity.getDate() }}</div>
                        <div>{{ activity.getTime() }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ul>
          <div v-if="isLoadingMore" class="flex items-center justify-center py-4">
            <div class="flex items-center justify-center p-4">
              <div class="h-6 w-6 animate-spin rounded-full border-2 border-solid border-b-primary"></div>
            </div>
          </div>
        </div>
        <div v-else class="mt-4 text-sm text-muted-foreground" :style="`max-height: ${height - 168}px;`">
          {{
            activitiesType
              ? t(`pages.customer.customerDetailPage.activities.empty.${activitiesType}`)
              : t('pages.customer.customerDetailPage.activities.empty.default')
          }}
        </div>
      </div>
    </div>
  </section>
</template>
